import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useRef } from 'react'

interface RefsContextType<T, TData> {
  setRef: (id: string, el: T | null, data?: TData) => void
  getRef: (id: string) => { el: T | null; data?: TData }
  removeRef: (id: string) => void
  clearRefs: () => void
}

export function createRefsContext<
  T extends HTMLElement,
  TData extends object,
>() {
  const RefsContext = createContext<RefsContextType<T, TData> | undefined>(
    undefined,
  )

  const Provider = ({ children }: { children: ReactNode }) => {
    const refs = useRef<Map<string, { el: T | null; data?: TData }>>(new Map())

    const setRef = useCallback((id: string, el: T | null, data?: TData) => {
      if (el) {
        refs.current.set(id, { el, data })
      }
    }, [])

    const getRef = useCallback((id: string): { el: T | null; data?: TData } => {
      return refs.current.get(id) || { el: null, data: undefined }
    }, [])

    const removeRef = useCallback((id: string) => {
      refs.current.delete(id)
    }, [])

    const clearRefs = useCallback(() => {
      refs.current.clear()
    }, [])

    return (
      <RefsContext.Provider value={{ setRef, getRef, removeRef, clearRefs }}>
        {children}
      </RefsContext.Provider>
    )
  }

  const useRefs = (): RefsContextType<T, TData> => {
    const context = useContext(RefsContext)
    if (!context) {
      throw new Error('useRefs must be used within its Provider')
    }
    return context
  }

  return { Provider, useRefs }
}
